/**
 * WinbackOfferStep - Inline step for displaying the winback offer in the paywall
 *
 * This is an inline variant of the modal-winback-offer component,
 * designed to render within the PaymentPaywallModal step flow.
 * Reuses the MembershipSummary component for consistent pricing display.
 */

import { useEffect, useMemo } from 'react';
import type { WinbackPricingResponse } from '@/api/generated-catch-backend/model';
import MembershipSummary from '@/app/components/ppra/components/ppra-payment-page/components/MembershipSummary';
import { buildPriceData } from '@/app/components/payment/components/method/payment-method-v2.controller';
import useAnalyticsCalls from '@/hooks/use-analytics';
import '../paywall-reset.css';

interface WinbackOfferStepProps {
  pricing: WinbackPricingResponse | null;
  isPricingLoading: boolean;
  onAccept: () => void;
  onDecline: () => void;
  isSubmitting: boolean;
  error: string | null;
}

export function WinbackOfferStep({
  pricing,
  isPricingLoading,
  onAccept,
  onDecline,
  isSubmitting,
  error,
}: WinbackOfferStepProps) {
  const { callModalViewed, callWinbackAccepted } = useAnalyticsCalls();

  const originalPriceCents = pricing?.originalPriceCents ?? 0;
  const discountCents = pricing?.discountCents ?? 0;
  const finalPriceCents = pricing?.finalPriceCents ?? 0;

  // Build price data for MembershipSummary component
  // Use subtotal - discount (without tax) for display consistency
  const priceData = useMemo(() => {
    if (!pricing) return buildPriceData(0, 0, 0, 0);
    return buildPriceData(originalPriceCents, discountCents, 0, originalPriceCents - discountCents);
  }, [pricing, originalPriceCents, discountCents]);

  // Track view (same as modal-winback-offer)
  useEffect(() => {
    if (pricing) {
      callModalViewed('Winback Offer', {
        original_price_cents: originalPriceCents.toString(),
        discount_cents: discountCents.toString(),
        final_price_cents: finalPriceCents.toString(),
        source: 'paywall',
      });
    }
  }, [
    pricing,
    originalPriceCents,
    discountCents,
    finalPriceCents,
    callModalViewed,
  ]);

  const handleAccept = () => {
    callWinbackAccepted(finalPriceCents, discountCents);
    onAccept();
  };

  return (
    <div className="paywall-reset flex flex-col gap-5">
      {/* Header - same messaging as modal-winback-offer */}
      <div className="flex flex-col gap-4">
        <h2 className="text-xl font-semibold tracking-[0.208px] text-slate-800">
          Thanks for giving Catch a try. Before you go...
        </h2>
        <p className="text-sm leading-5 tracking-[0.208px] text-slate-600">
          Your risk changes over time. Canceling now means you won't be alerted
          to those changes.
        </p>
        <p className="text-sm leading-5 tracking-[0.208px] text-slate-600">
          Since you've already begun your journey, we're offering you a
          discounted rate to continue.
        </p>
      </div>

      {/* Error display */}
      {error && (
        <div className="rounded-lg bg-red-50 p-3 text-center text-sm text-red-600">
          {error}
        </div>
      )}

      {/* Membership pricing card - same component as info step */}
      <MembershipSummary
        priceData={priceData}
        isCouponVisible={false}
        showCoupon={() => undefined}
        coupon=""
        setCoupon={() => undefined}
        couponValidationError={null}
        isSubmitting={false}
        getPrice={() => Promise.resolve()}
        isSinglePaymentEnabled={false}
        isTrial={false}
        isPriceLoading={isPricingLoading}
      />

      {/* Action buttons */}
      <button
        onClick={handleAccept}
        disabled={isSubmitting || isPricingLoading}
        className="flex w-full items-center justify-center rounded-full bg-[#7CF1C7] px-5 py-5 text-sm font-bold leading-[1.4] text-[#183344] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        data-testid="accept-winback-button"
      >
        {isSubmitting ? 'Processing...' : 'Claim Discount'}
      </button>
      <button
        onClick={onDecline}
        disabled={isSubmitting}
        className="flex w-full items-center justify-center rounded-full bg-[#183344] px-5 py-5 text-sm font-bold leading-[1.4] text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        data-testid="decline-winback-button"
      >
        Cancel My Subscription
      </button>
    </div>
  );
}
